import {
  clienteSchema,
  listaDeClientesSchema,
  listaDeMembrosSchema,
  linkDeSenhaEmitidoSchema,
  membroComConviteSchema,
  type AdicionarMembro,
  type AtualizarCliente,
  type Cliente,
  type ListaDeMembros,
  type MembroComConvite,
  type PapelNoCliente,
} from "@base/contracts";
import { z } from "zod";
import type { ClienteHttp } from "../../../shared/http/cliente-http";

const semCorpo = z.null();
const caminhoDoCliente = (id: string) => `/v1/clientes/${encodeURIComponent(id)}`;

/** Porta de saida do modulo de clientes no front. */
export interface ClientesGateway {
  listar(): Promise<readonly Cliente[]>;
  criar(nome: string): Promise<Cliente>;
  atualizar(id: string, dados: AtualizarCliente): Promise<Cliente>;
  excluir(id: string): Promise<void>;
  membros(clienteId: string): Promise<ListaDeMembros>;
  adicionarMembro(clienteId: string, dados: AdicionarMembro): Promise<MembroComConvite>;
  enviarLinkDeSenha(clienteId: string, usuarioId: string): Promise<{ linkDeSenha: string }>;
  alterarPapel(clienteId: string, usuarioId: string, papel: PapelNoCliente): Promise<void>;
  removerMembro(clienteId: string, usuarioId: string): Promise<void>;
}

export class ClientesGatewayHttp implements ClientesGateway {
  constructor(private readonly http: ClienteHttp) {}

  async listar(): Promise<readonly Cliente[]> {
    return (await this.http.requisitar("/v1/clientes", listaDeClientesSchema)).dados;
  }

  criar(nome: string): Promise<Cliente> {
    return this.http.requisitar("/v1/clientes", clienteSchema, { metodo: "POST", corpo: { nome } });
  }

  atualizar(id: string, dados: AtualizarCliente): Promise<Cliente> {
    return this.http.requisitar(caminhoDoCliente(id), clienteSchema, { metodo: "PATCH", corpo: dados });
  }

  async excluir(id: string): Promise<void> {
    await this.http.requisitar(caminhoDoCliente(id), semCorpo, { metodo: "DELETE" });
  }

  membros(clienteId: string): Promise<ListaDeMembros> {
    return this.http.requisitar(`${caminhoDoCliente(clienteId)}/membros`, listaDeMembrosSchema);
  }

  adicionarMembro(clienteId: string, dados: AdicionarMembro): Promise<MembroComConvite> {
    return this.http.requisitar(`${caminhoDoCliente(clienteId)}/membros`, membroComConviteSchema, {
      metodo: "POST",
      corpo: dados,
    });
  }

  enviarLinkDeSenha(clienteId: string, usuarioId: string): Promise<{ linkDeSenha: string }> {
    return this.http.requisitar(
      `${caminhoDoCliente(clienteId)}/membros/${encodeURIComponent(usuarioId)}/link-de-senha`,
      linkDeSenhaEmitidoSchema,
      { metodo: "POST" },
    );
  }

  async alterarPapel(clienteId: string, usuarioId: string, papel: PapelNoCliente): Promise<void> {
    await this.http.requisitar(
      `${caminhoDoCliente(clienteId)}/membros/${encodeURIComponent(usuarioId)}`,
      semCorpo,
      { metodo: "PATCH", corpo: { papel } },
    );
  }

  async removerMembro(clienteId: string, usuarioId: string): Promise<void> {
    await this.http.requisitar(
      `${caminhoDoCliente(clienteId)}/membros/${encodeURIComponent(usuarioId)}`,
      semCorpo,
      { metodo: "DELETE" },
    );
  }
}
